import { Fragment } from 'react';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { KeenIcon } from '@/components';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  useGetHelpdeskFAQByIdQuery,
  useDeleteHelpdeskFAQMutation,
} from '@/services/HelpdeskFAQService';
import { usePermissions } from '@/hooks/usePermissions';
import { useState } from 'react';
import { ConfirmationDialog } from '@/components/dialogBox';
import { languageOptions } from '@/types/cms.types';
import { formatDisplayDateTime } from '@/utils/Date';

const HelpdeskFAQView = () => {
  const { currentLayout } = useLayout();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();

  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('en');

  const { data: faqData, isLoading } = useGetHelpdeskFAQByIdQuery(id || '', {
    skip: !id,
  });

  const [deleteFAQ] = useDeleteHelpdeskFAQMutation();

  const handleDelete = async () => {
    if (!id) return;

    try {
      await deleteFAQ(id).unwrap();
      enqueueSnackbar('FAQ deleted successfully', { variant: 'success' });
      navigate('/helpdesk-faq');
    } catch {
      enqueueSnackbar('Failed to delete FAQ', { variant: 'error' });
    }
  };

  if (isLoading) {
    return (
      <div className="container-fixed">
        <div className="card">
          <div className="card-body">
            <div className="text-center py-4">Loading FAQ...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!faqData?.data) {
    return (
      <div className="container-fixed">
        <div className="card">
          <div className="card-body">
            <div className="text-center py-4 text-red-500">FAQ not found</div>
          </div>
        </div>
      </div>
    );
  }

  const faq = faqData.data;
  const selectedVariant = faq.translation_variants?.find(
    (variant) => variant.language === selectedLanguage
  );
  const currentTranslation = selectedVariant?.draft ||
    selectedVariant?.published || {
      title:
        faq.translations?.[selectedLanguage]?.title ||
        faq.translations?.[faq.language]?.title ||
        faq.title,
      body:
        faq.translations?.[selectedLanguage]?.body ||
        faq.translations?.[faq.language]?.body ||
        faq.body,
    };

  return (
    <Fragment>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle text="View FAQ" />
              <ToolbarDescription>
                FAQ details and translations
              </ToolbarDescription>
            </ToolbarHeading>
            <ToolbarActions>
              {/* Language Selector */}
              <div className="relative">
                <select
                  value={selectedLanguage}
                  onChange={(e) => setSelectedLanguage(e.target.value)}
                  className="select select-sm select-bordered pl-8 pr-8 h-10"
                  aria-label="Select language"
                >
                  {languageOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <img
                  src="/media/flags/globe.svg"
                  alt="Globe"
                  className="absolute dark:brightness-0 dark:invert contrast:brightness-0 dark:invert left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 invert pointer-events-none"
                  aria-hidden="true"
                />
              </div>
              <button
                className="btn btn-sm btn-light flex items-center gap-2 h-10"
                onClick={() => navigate('/helpdesk-faq')}
              >
                <KeenIcon icon="arrow-left" />
                Back to List
              </button>
              {hasPermission('faq_management_update') && (
                <button
                  className="btn btn-sm btn-primary flex items-center gap-2 h-10"
                  onClick={() => navigate(`/helpdesk-faq/edit/${id}`)}
                >
                  <KeenIcon icon="pencil" className="text-white" />
                  Edit FAQ
                </button>
              )}
            </ToolbarActions>
          </Toolbar>
        </Container>
      )}

      <div className="container-fixed">
        {/* Main FAQ Content */}
        <div className="card mb-5">
          <div className="card-header">
            <h2 className="card-title">FAQ Details</h2>
            <div className="flex items-center gap-4">
              {/* Helpful/Not Helpful Counts */}
              <div className="flex items-center gap-2">
                <span className="badge badge-outline badge-success contrast:text-green-100 gap-2">
                  <KeenIcon icon="like" className="text-xs" />
                  {faq.is_helpful_count || 0}
                </span>
                <span className="badge badge-outline gap-2">
                  <KeenIcon icon="dislike" className="text-xs" />
                  {faq.is_not_helpful_count || 0}
                </span>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="space-y-6">
              {selectedVariant && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-coal-100 dark:bg-coal-500 contrast:border-coal-100 contrast:bg-coal-500">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-100 contrast:text-white">
                        Translation Status
                      </p>
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 contrast:text-white/80">
                        {selectedVariant.last_updated_label
                          ? `${selectedVariant.last_updated_label} • ${
                              selectedVariant.last_updated_at
                                ? formatDisplayDateTime(
                                    selectedVariant.last_updated_at
                                  )
                                : 'No timestamp'
                            }`
                          : 'No workflow activity yet'}
                      </p>
                    </div>
                    <span className="badge badge-outline">
                      {selectedVariant.status.toUpperCase()}
                    </span>
                  </div>
                  {selectedVariant.status === 'draft' &&
                    selectedVariant.has_published_version && (
                      <p className="mt-3 text-xs text-amber-700 dark:text-amber-200 contrast:text-amber-100">
                        This language currently has an unpublished draft. The
                        published version remains live until an editor publishes
                        the draft.
                      </p>
                    )}
                </div>
              )}

              {/* Title */}
              <div className="space-y-2">
                <span id="title-label" className="form-label font-semibold">
                  Title
                </span>
                <div
                  id="title"
                  className="text-lg text-gray-800 dark:text-gray-100 contrast:text-white"
                  aria-labelledby="title-label"
                  role="text"
                >
                  {currentTranslation.title || 'No title'}
                </div>
              </div>

              {/* Body */}
              <div className="space-y-2">
                <span id="body-label" className="form-label font-semibold">
                  Body Content
                </span>
                <div
                  id="body"
                  className="whitespace-pre-wrap rounded border border-gray-200 bg-gray-100 p-4 dark:border-coal-100 dark:bg-coal-500 dark:text-gray-100 contrast:border-coal-100 contrast:bg-coal-500 contrast:text-white"
                  aria-labelledby="body-label"
                  role="text"
                >
                  {currentTranslation.body || 'No content'}
                </div>
              </div>

              {/* Timestamps */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <span
                    id="created-at-label"
                    className="form-label text-sm text-gray-600"
                  >
                    Created At
                  </span>
                  <div
                    id="created-at"
                    className="text-gray-800"
                    aria-labelledby="created-at-label"
                    role="text"
                  >
                    {formatDisplayDateTime(faq.created_at, { hour12: false })}
                  </div>
                </div>
                <div>
                  <span
                    id="updated-at-label"
                    className="form-label text-sm text-gray-600"
                  >
                    Updated At
                  </span>
                  <div
                    id="updated-at"
                    className="text-gray-800"
                    aria-labelledby="updated-at-label"
                    role="text"
                  >
                    {formatDisplayDateTime(faq.updated_at, { hour12: false })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Translations */}
        {/* {faq.translations && Object.keys(faq.translations).length > 0 && (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Translations</h3>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                {Object.entries(faq.translations).map(([lang, translation]) => (
                  <div
                    key={lang}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
                      <span className="badge badge-sm badge-outline uppercase">
                        {lang}
                      </span>
                    </div>
                    <div className="p-4 space-y-3">
                      <div>
                        <label className="form-label text-sm font-semibold">
                          Title
                        </label>
                        <div className="text-gray-800">{translation.title}</div>
                      </div>
                      <div>
                        <label className="form-label text-sm font-semibold">
                          Body
                        </label>
                        <div className="p-3 bg-gray-50 rounded border border-gray-200 whitespace-pre-wrap">
                          {translation.body}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )} */}
      </div>

      <ConfirmationDialog
        open={deleteModal}
        onClose={() => setDeleteModal(false)}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this FAQ? This action cannot be undone."
      />
    </Fragment>
  );
};

export { HelpdeskFAQView };
